/**
 * Data Upload Page (Admin)
 * Upload local market prices and bulk historical data (FR-200, FR-700)
 * Wired to admin-svc: template download, validate (dry-run), process (upsert), history.
 */

import React, { useState, useRef } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Button,
  Chip,
  LinearProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Tabs,
  Tab,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { PageHeader, StatCard, LoadingSkeleton, EmptyState } from '../../components/common';
import UploadDetailDialog from '../../components/admin/UploadDetailDialog';
import { IssueTable, CapCaption, uploadStatusChipSx } from '../../components/admin/uploadUi';
import { useUploadHistory } from '../../services/queries';
import { downloadTemplate, validateUpload, processUpload, MAX_UPLOAD_BYTES } from '../../services/admin';
import { useThemeMode, MONO, radius } from '../../context/ThemeContext';
import { useRecipes } from '../../theme/recipes';
import { fmtDate } from '../../utils/format';
import { exportCsv } from '../../utils/exportCsv';

const firaCode = { fontFamily: MONO };
const UPLOAD_STEPS = ['Select File', 'Validate Data', 'Process Records', 'Complete'];

/** Table of parsed rows headed for insert or update (update rows show the price change). */
function PreviewRowsTable({ rows, showOldPrice }) {
  const { palette } = useThemeMode();
  const recipes = useRecipes();
  const mono = { fontFamily: MONO, fontSize: '0.8rem' };
  return (
    <TableContainer sx={{ maxHeight: 320, border: `1px solid ${palette.divider}`, borderRadius: '10px' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={recipes.headCellSx}>Sheet</TableCell>
            <TableCell align="right" sx={recipes.headCellSx}>Row</TableCell>
            <TableCell sx={recipes.headCellSx}>Market</TableCell>
            <TableCell sx={recipes.headCellSx}>Commodity</TableCell>
            <TableCell sx={recipes.headCellSx}>Date</TableCell>
            <TableCell align="right" sx={recipes.headCellSx}>Bag</TableCell>
            <TableCell sx={recipes.headCellSx}>Season</TableCell>
            {showOldPrice && <TableCell align="right" sx={recipes.headCellSx}>Current ₦</TableCell>}
            <TableCell align="right" sx={recipes.headCellSx}>{showOldPrice ? 'New ₦' : 'Price ₦'}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} sx={recipes.bodyRowSx}>
              <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 110, fontSize: '0.82rem' }}>{r.sheet}</Typography></TableCell>
              <TableCell align="right"><Box component="span" sx={mono}>{r.row}</Box></TableCell>
              <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 130, fontSize: '0.82rem' }}>{r.market}</Typography></TableCell>
              <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 130, fontSize: '0.82rem' }}>{r.commodity}</Typography></TableCell>
              <TableCell><Box component="span" sx={mono}>{fmtDate(r.date)}</Box></TableCell>
              <TableCell align="right"><Box component="span" sx={mono}>{r.bagSize}</Box></TableCell>
              <TableCell><Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{r.season}</Typography></TableCell>
              {showOldPrice && (
                <TableCell align="right">
                  <Box component="span" sx={{ ...mono, color: palette.textMuted }}>
                    {r.oldPrice != null ? Number(r.oldPrice).toLocaleString() : '·'}
                  </Box>
                </TableCell>
              )}
              <TableCell align="right">
                <Box
                  component="span"
                  sx={{
                    ...mono,
                    fontWeight: 600,
                    color:
                      showOldPrice && r.oldPrice != null && Number(r.price) !== Number(r.oldPrice)
                        ? Number(r.price) > Number(r.oldPrice)
                          ? palette.danger
                          : palette.accent
                        : palette.text,
                  }}
                >
                  {Number(r.price).toLocaleString()}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Row-level preview tabs: what will insert, what will overwrite, what failed, what was fuzzy-matched. */
function PreviewTabs({ summary, fileName }) {
  const { palette } = useThemeMode();
  const recipes = useRecipes();
  const [tab, setTab] = useState(0);
  const preview = summary.preview || {};
  const inserts = preview.inserts || [];
  const updates = preview.updates || [];
  const errors = summary.errorDetails || [];
  const warnings = summary.warningDetails || [];
  const insertTotal = preview.insertTotal ?? inserts.length;
  const updateTotal = preview.updateTotal ?? updates.length;

  const handleExportErrors = () => {
    exportCsv(
      `${(fileName || 'upload').replace(/\.[^.]+$/, '')}-errors.csv`,
      ['Sheet', 'Row', 'Detail'],
      errors.map((d) => [d.sheet, d.row, d.message])
    );
  };

  const emptyMsg = (msg) => (
    <Box sx={{ ...recipes.emptyBoxSx, py: 4 }}>
      <Typography variant="body2" sx={{ color: palette.textMuted }}>{msg}</Typography>
    </Box>
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={recipes.tabsSx} variant="scrollable" allowScrollButtonsMobile>
        <Tab label={`New rows (${insertTotal.toLocaleString()})`} />
        <Tab label={`Updates (${updateTotal.toLocaleString()})`} />
        <Tab label={`Errors (${(summary.errors ?? 0).toLocaleString()})`} />
        <Tab label={`Warnings (${(summary.warnings ?? 0).toLocaleString()})`} />
      </Tabs>
      <Box sx={{ mt: 2 }}>
        {tab === 0 && (
          inserts.length ? (
            <Box>
              <PreviewRowsTable rows={inserts} showOldPrice={false} />
              <CapCaption shown={inserts.length} total={insertTotal} noun="new rows" />
            </Box>
          ) : emptyMsg('No new rows - every valid row matches an existing record.')
        )}
        {tab === 1 && (
          updates.length ? (
            <Box>
              <Typography variant="caption" sx={{ color: palette.textMuted, display: 'block', mb: 1 }}>
                These rows match an existing record (market, commodity, date, bag, season) and will overwrite its price.
              </Typography>
              <PreviewRowsTable rows={updates} showOldPrice />
              <CapCaption shown={updates.length} total={updateTotal} noun="updates" />
            </Box>
          ) : emptyMsg('No overwrites - all valid rows are new records.')
        )}
        {tab === 2 && (
          errors.length ? (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button
                  size="small"
                  startIcon={<Icon icon="ph:export" width={15} height={15} />}
                  onClick={handleExportErrors}
                  sx={{ textTransform: 'none', fontWeight: 600, color: palette.accent, '&:hover': { bgcolor: palette.accentBg } }}
                >
                  Export CSV
                </Button>
              </Box>
              <IssueTable rows={errors} tone="danger" maxHeight={320} />
              <CapCaption shown={errors.length} total={summary.errors} noun="errors (export contains the listed rows)" />
            </Box>
          ) : emptyMsg('No errors - every row parsed cleanly.')
        )}
        {tab === 3 && (
          warnings.length ? (
            <Box>
              <Typography variant="caption" sx={{ color: palette.textMuted, display: 'block', mb: 1 }}>
                Warnings are non-blocking: fuzzy name matches and unusual bag sizes. Check they matched as intended.
              </Typography>
              <IssueTable rows={warnings} tone="warning" maxHeight={320} />
              <CapCaption shown={warnings.length} total={summary.warnings} noun="warnings" />
            </Box>
          ) : emptyMsg('No warnings - all names matched exactly.')
        )}
      </Box>
    </Box>
  );
}

function ResultPanel({ summary, fileName }) {
  const { palette } = useThemeMode();
  const details = summary.errorDetails || [];
  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <StatCard label="Processed" value={(summary.recordsProcessed ?? 0).toLocaleString()} tone="info" icon={<Icon icon="ph:stack" width={20} height={20} />} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Inserted" value={(summary.inserted ?? 0).toLocaleString()} tone="accent" icon={<Icon icon="ph:plus-circle" width={20} height={20} />} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Updated" value={(summary.updated ?? 0).toLocaleString()} tone="teal" icon={<Icon icon="ph:arrows-clockwise" width={20} height={20} />} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Errors" value={(summary.errors ?? 0).toLocaleString()} tone={summary.errors > 0 ? 'danger' : 'neutral'} icon={<Icon icon="ph:warning-circle" width={20} height={20} />} />
        </Grid>
      </Grid>
      <Typography variant="caption" sx={{ color: palette.textMuted, display: 'block', mt: 1.5 }}>
        {summary.skipped} skipped (duplicates / filtered) · {summary.warnings} warnings
      </Typography>
      {summary.retiredRows > 0 && (
        <Alert
          severity="warning"
          sx={{ mt: 1.5 }}
          action={
            <Button component={RouterLink} to="/admin/commodities" size="small" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
              Open catalog
            </Button>
          }
        >
          {summary.retiredRows === 1
            ? '1 row names something retired and was not imported.'
            : `${summary.retiredRows} rows name something retired and were not imported.`}{' '}
          The catalog, not the file, is why. Restore the entry to accept this data, or leave
          it retired and ignore these rows.
        </Alert>
      )}
      {summary.preview ? (
        <PreviewTabs summary={summary} fileName={fileName} />
      ) : (
        details.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Errors</Typography>
            <IssueTable rows={details} tone="danger" maxHeight={280} />
            <CapCaption shown={details.length} total={summary.errors} noun="errors" />
          </Box>
        )
      )}
    </Box>
  );
}

function DataUpload() {
  const { palette } = useThemeMode();
  const recipes = useRecipes();
  const queryClient = useQueryClient();
  const { data: history = [], isLoading: historyLoading } = useUploadHistory();
  const fileInputRef = useRef(null);

  const [uploadMode, setUploadMode] = useState(null);
  const [pendingMode, setPendingMode] = useState(null);
  const [file, setFile] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [validation, setValidation] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [detailId, setDetailId] = useState(null);

  const ctaSx = { ...recipes.primaryBtnSx, px: 2.5, py: 0.9, fontSize: '0.875rem', textTransform: 'none' };

  const pickFile = (mode) => {
    setPendingMode(mode);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const onFileChosen = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_UPLOAD_BYTES) {
      setError(`"${f.name}" is ${(f.size / (1024 * 1024)).toFixed(1)}MB - the limit is 15MB. Split the file and upload in parts.`);
      return;
    }
    const mode = pendingMode || 'weekly';
    setUploadMode(mode);
    setFile(f);
    setActiveStep(1);
    setBusy(true);
    setError('');
    setValidation(null);
    setResult(null);
    try {
      const summary = await validateUpload(f, mode);
      setValidation(summary);
    } catch (err) {
      setError(err?.message || 'Validation failed.');
      setActiveStep(0);
      setUploadMode(null);
    } finally {
      setBusy(false);
    }
  };

  const runProcess = async () => {
    if (!file) return;
    setActiveStep(2);
    setBusy(true);
    setError('');
    try {
      const summary = await processUpload(file, uploadMode);
      setResult(summary);
      setActiveStep(3);
      queryClient.invalidateQueries({ queryKey: ['upload-history'] });
      queryClient.invalidateQueries({ queryKey: ['upload-history-paged'] });
    } catch (err) {
      setError(err?.message || 'Processing failed.');
      setActiveStep(1);
    } finally {
      setBusy(false);
    }
  };

  const handleReset = () => {
    setUploadMode(null);
    setPendingMode(null);
    setFile(null);
    setActiveStep(0);
    setValidation(null);
    setResult(null);
    setError('');
  };

  const handleDownloadTemplate = async () => {
    setError('');
    try {
      await downloadTemplate();
    } catch (err) {
      setError(err?.message || 'Template download failed.');
    }
  };

  // Accessible click-card for the two upload modes
  const modeCardSx = {
    cursor: 'pointer',
    height: '100%',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': { borderColor: palette.accent, boxShadow: palette.shadowMd },
    '&:focus-visible': recipes.focusRingSx,
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Admin"
        title="Data Upload"
        subtitle="Upload local market prices and bulk historical data"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Admin', path: '/admin/users' },
          { label: 'Data Upload' },
        ]}
      />

      {/* Hidden file input shared by both upload modes */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={onFileChosen}
        aria-label="Choose Excel file to upload"
      />

      <Grid container spacing={3}>
        {/* Upload Section */}
        <Grid item xs={12} lg={8}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          {!uploadMode ? (
            <Box>
              {/* Download Template Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2.5, gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 44, height: 44, flexShrink: 0,
                        borderRadius: `${radius.sm}px`,
                        bgcolor: palette.accentBg, color: palette.accent,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <Icon icon="ph:download-simple" width={22} height={22} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>Download Upload Template</Typography>
                      <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                        Pre-formatted Excel file with all markets and column headers. Fill in your price data and upload.
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={<Icon icon="ph:download-simple" width={16} height={16} />}
                    onClick={handleDownloadTemplate}
                    sx={{ ...ctaSx, whiteSpace: 'nowrap' }}
                  >
                    Download Template
                  </Button>
                </CardContent>
              </Card>

              {/* Upload Type Selection */}
              <Grid container spacing={3}>
                {[
                  {
                    mode: 'weekly',
                    icon: 'ph:cloud-arrow-up',
                    title: 'Weekly Price Upload',
                    desc: 'Upload regular weekly market price data from multi-sheet Excel files',
                    chip: 'Recommended for regular updates',
                  },
                  {
                    mode: 'bulk',
                    icon: 'ph:clock-counter-clockwise',
                    title: 'Bulk Historical Import',
                    desc: 'Import multiple years of historical data in one file',
                    chip: 'For backfilling historical data',
                  },
                ].map((opt) => (
                  <Grid item xs={12} md={6} key={opt.mode}>
                    <Card
                      role="button"
                      tabIndex={0}
                      aria-label={`${opt.title}: ${opt.desc}`}
                      onClick={() => pickFile(opt.mode)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          pickFile(opt.mode);
                        }
                      }}
                      sx={modeCardSx}
                    >
                      <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <Box
                          sx={{
                            width: 64, height: 64, mx: 'auto', mb: 2,
                            borderRadius: `${radius.sm}px`,
                            bgcolor: palette.accentBg, color: palette.accent,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <Icon icon={opt.icon} width={34} height={34} />
                        </Box>
                        <Typography variant="h6" gutterBottom>{opt.title}</Typography>
                        <Typography variant="body2" sx={{ color: palette.textSecondary, mb: 2 }}>
                          {opt.desc}
                        </Typography>
                        <Chip
                          label={opt.chip}
                          variant="outlined"
                          sx={{ borderColor: palette.accent, color: palette.accent, fontWeight: 600 }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <Card>
              <CardHeader
                title={uploadMode === 'weekly' ? 'Weekly Price Upload' : 'Bulk Historical Import'}
                titleTypographyProps={{ variant: 'h6' }}
                subheader={file?.name}
                action={
                  (activeStep === 3 || activeStep === 1) && (
                    <Button
                      startIcon={<Icon icon="ph:arrows-clockwise" width={16} height={16} />}
                      onClick={handleReset}
                      sx={{ textTransform: 'none', fontWeight: 600, color: palette.accent, '&:hover': { bgcolor: palette.accentBg } }}
                    >
                      New Upload
                    </Button>
                  )
                }
              />
              <CardContent>
                <Stepper
                  activeStep={activeStep}
                  sx={{
                    mb: 4,
                    '& .MuiStepIcon-root': { color: palette.surfaceHover, '& .MuiStepIcon-text': { fill: palette.textSecondary } },
                    '& .MuiStepIcon-root.Mui-active': { color: palette.accent, '& .MuiStepIcon-text': { fill: '#fff' } },
                    '& .MuiStepIcon-root.Mui-completed': { color: palette.accent },
                    '& .MuiStepLabel-label': { fontSize: '0.82rem', color: palette.textSecondary },
                    '& .MuiStepLabel-label.Mui-active': { color: palette.text, fontWeight: 600 },
                  }}
                >
                  {UPLOAD_STEPS.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                {busy && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {activeStep === 1 ? 'Validating file…' : 'Processing records…'}
                    </Typography>
                    <LinearProgress
                      sx={{ height: 8, borderRadius: 4, bgcolor: palette.surfaceHover, '& .MuiLinearProgress-bar': { bgcolor: palette.accent } }}
                    />
                  </Box>
                )}

                {/* Validation result + confirm */}
                {!busy && validation && activeStep === 1 && (
                  <Box>
                    <Alert severity={validation.errors > 0 ? 'warning' : 'info'} sx={{ mb: 3 }}>
                      {validation.errors > 0
                        ? `Validation found ${validation.errors} row error(s) - they'll be skipped. ${validation.inserted + validation.updated} valid row(s) ready to process.`
                        : `${validation.inserted + validation.updated} valid row(s) ready to process.`}
                    </Alert>
                    {(validation.unresolvedCommodities?.length > 0 || validation.unresolvedMarkets?.length > 0) && (
                      <Alert
                        severity="warning"
                        sx={{ mb: 2 }}
                        action={
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Button
                              component={RouterLink}
                              to="/admin/aliases"
                              size="small"
                              sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                            >
                              Add aliases
                            </Button>
                            {validation.unresolvedMarkets?.length > 0 && (
                              <Button
                                component={RouterLink}
                                to="/admin/markets"
                                size="small"
                                sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                              >
                                Add market
                              </Button>
                            )}
                          </Box>
                        }
                      >
                        Unknown names were dropped:
                        {validation.unresolvedCommodities?.length > 0 &&
                          ` ${validation.unresolvedCommodities.length} commodit${validation.unresolvedCommodities.length === 1 ? 'y' : 'ies'} (${validation.unresolvedCommodities.slice(0, 4).map((u) => u.name).join(', ')}${validation.unresolvedCommodities.length > 4 ? ', …' : ''})`}
                        {validation.unresolvedCommodities?.length > 0 && validation.unresolvedMarkets?.length > 0 && ' and'}
                        {validation.unresolvedMarkets?.length > 0 &&
                          ` ${validation.unresolvedMarkets.length} market sheet${validation.unresolvedMarkets.length === 1 ? '' : 's'} (${validation.unresolvedMarkets.slice(0, 4).map((u) => u.name).join(', ')}${validation.unresolvedMarkets.length > 4 ? ', …' : ''})`}
                        .{' '}
                        {validation.unresolvedMarkets?.length > 0
                          ? 'If a name is a new spelling of something you already track, add an alias. If it is a market you have never collected in before, add the market. Then re-upload to capture those rows.'
                          : 'Add aliases, then re-upload to capture those rows.'}
                      </Alert>
                    )}
                    <ResultPanel summary={validation} fileName={file?.name} />
                    <Box sx={{ mt: 3, display: 'flex', gap: 1.5 }}>
                      <Button
                        variant="contained"
                        disableElevation
                        onClick={runProcess}
                        disabled={validation.inserted + validation.updated === 0}
                        sx={ctaSx}
                      >
                        Process {(validation.inserted + validation.updated).toLocaleString()} records
                      </Button>
                      <Button onClick={handleReset} sx={{ color: palette.textMuted, textTransform: 'none' }}>Cancel</Button>
                    </Box>
                  </Box>
                )}

                {/* Final result */}
                {!busy && result && activeStep === 3 && (
                  <Box>
                    <Alert severity={result.errors > 0 ? 'warning' : 'success'} sx={{ mb: 3 }}>
                      {result.errors > 0
                        ? `Upload completed with ${result.errors} row error(s).`
                        : 'Upload completed successfully!'}
                    </Alert>
                    <ResultPanel summary={result} fileName={file?.name} />
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Upload History */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardHeader
              title="Recent Uploads"
              titleTypographyProps={{ variant: 'h6' }}
              action={
                <Button
                  component={RouterLink}
                  to="/admin/upload/history"
                  size="small"
                  endIcon={<Icon icon="ph:arrow-right" width={14} height={14} />}
                  sx={{ textTransform: 'none', fontWeight: 600, color: palette.accent, '&:hover': { bgcolor: palette.accentBg } }}
                >
                  View all
                </Button>
              }
            />
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              {historyLoading ? (
                <Box sx={{ p: 2 }}>
                  <LoadingSkeleton variant="table" rows={4} />
                </Box>
              ) : history.length === 0 ? (
                <EmptyState
                  compact
                  variant="dashed"
                  icon={<Icon icon="ph:file-xls" width={24} height={24} />}
                  title="No uploads yet"
                  description="Uploads you run will appear here with their status."
                  sx={{ m: 2 }}
                />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={recipes.headCellSx}>File</TableCell>
                        <TableCell align="right" sx={recipes.headCellSx}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {history.slice(0, 8).map((upload) => (
                        <TableRow
                          key={upload.id}
                          hover
                          tabIndex={0}
                          role="button"
                          aria-label={`View details of ${upload.filename}`}
                          onClick={() => setDetailId(upload.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setDetailId(upload.id);
                            }
                          }}
                          sx={{ ...recipes.bodyRowSx, cursor: 'pointer', '&:focus-visible': recipes.focusRingSx }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Icon icon="ph:file-xls" width={18} height={18} color={palette.textSecondary} />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 190 }}>
                                  {upload.filename}
                                </Typography>
                                <Typography variant="caption" sx={{ color: palette.textMuted }}>
                                  {fmtDate(upload.uploadDate)} · <Box component="span" sx={firaCode}>{(upload.recordsProcessed || 0).toLocaleString()}</Box> records
                                  {upload.errors > 0 && (
                                    <Box component="span" sx={{ color: palette.danger }}> · {upload.errors} errors</Box>
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Chip size="small" label={upload.status} sx={uploadStatusChipSx(palette, upload.status)} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Upload Guidelines */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Upload Guidelines" titleTypographyProps={{ variant: 'h6' }} />
            <CardContent>
              <Grid container spacing={3}>
                {[
                  { title: 'File Requirements', items: ['Excel format (.xlsx)', 'One sheet per market', 'Sheet names match market names', 'Max file size: 15MB'] },
                  { title: 'Data Validation', items: ['Prices must be positive numbers', 'Valid date formats', 'Commodity alias resolution', 'Duplicate detection'] },
                  { title: 'Processing Rules', items: ['Auto-calculate price per KG', 'Filter out fertilizer data', 'Fuzzy market name matching', 'Detailed error reporting'] },
                ].map((col) => (
                  <Grid item xs={12} md={4} key={col.title}>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: palette.accent, fontWeight: 700 }}>{col.title}</Typography>
                    <List dense>
                      {col.items.map((t) => (
                        <ListItem key={t} disableGutters>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <Icon icon="ph:check-circle" width={16} height={16} color={palette.accent} />
                          </ListItemIcon>
                          <ListItemText primary={t} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <UploadDetailDialog uploadId={detailId} onClose={() => setDetailId(null)} />
    </Box>
  );
}

export default DataUpload;
